import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  PlusIcon, 
  MapPinIcon,
  PhoneIcon,
  UserIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowLeftIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import googleMapsLoader from '../utils/googleMapsLoader';
import { getCustomerPortalErrorMessage } from '../utils/apiErrorUtils';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const DEFAULT_LOCATION_NAME = 'Medical Facility';

// SearchableSelect component
const SearchableSelect = ({ 
  label, 
  value, 
  onChange, 
  options = [], 
  placeholder = "Search...", 
  className = '', 
  required = false, 
  disabled = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    String(option.label ?? '')
      .toLowerCase()
      .includes((searchTerm || '').toLowerCase())
  );

  // Set search term when value changes (for external updates)
  useEffect(() => {
    if (value) {
      const selectedOption = options.find(option => option.value === value);
      if (selectedOption) {
        setSearchTerm(selectedOption.label);
      }
    } else {
      setSearchTerm('');
    }
  }, [value, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (option) => {
    if (option && option.value !== undefined) {
      onChange({ target: { value: option.value } });
      setSearchTerm(option.label || '');
      setIsOpen(false);
    }
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {isOpen && filteredOptions.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            {filteredOptions.map((option) => (
              <div
                key={option.value}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => handleSelect(option)}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// UI Components
const Button = ({ children, onClick, variant = 'primary', disabled = false, className = '' }) => {
  const baseClasses = 'px-4 py-2 rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';
  const variants = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
    secondary: 'bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-gray-500',
    success: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500'
  };
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
    >
      {children}
    </button>
  );
};

const Input = ({ value, onChange, placeholder, type = 'text', className = '', label, required = false, disabled = false, readOnly = false }) => (
  <div className={className}>
    {label && (
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
    )}
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      readOnly={readOnly}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    />
  </div>
);

const ServiceLocations = () => {
  const { makeAuthenticatedRequest } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationContacts, setLocationContacts] = useState([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    phone: '',
    title: ''
  });
  const [mapsInitialized, setMapsInitialized] = useState(false);
  const [autocomplete, setAutocomplete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    address_2: '',
    phone_number: '',
    email_address: '',
    city: '',
    state: '',
    zip_code: '',
    latitude: '',
    longitude: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const addressAutocompleteRef = useRef(null);

  useEffect(() => {
    loadLocations();
    initializeGoogleMaps();
  }, []);

  useEffect(() => {
    if (showModal && mapsInitialized) {
      setTimeout(() => {
        setupAddressAutocomplete();
      }, 100);
    }
  }, [showModal, mapsInitialized]);

  const initializeGoogleMaps = async () => {
    try {
      await googleMapsLoader.load(['places']);
      setMapsInitialized(true);
    } catch (error) {
      console.error('Error loading Google Maps:', error);
      setMapsInitialized(false);
    }
  };

  const loadLocations = async () => {
    try {
      setLoading(true);
      const response = await makeAuthenticatedRequest('/service-locations');
      
      if (response.success) {
        setLocations(response.data);
      } else {
        toast.error(response.message || 'Failed to load service locations');
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to load service locations'));
    } finally {
      setLoading(false);
    }
  };

  const loadLocationDetails = async (locationId) => {
    try {
      const response = await makeAuthenticatedRequest(`/service-locations/${locationId}`);
      if (response.success) {
        setSelectedLocation(response.data);
        if (response.data.location_contacts && Array.isArray(response.data.location_contacts)) {
          setLocationContacts(response.data.location_contacts);
        } else {
          setLocationContacts([]);
        }
      } else {
        toast.error(response.message || 'Failed to load location details');
      }
    } catch (error) {
      console.error('Error loading location details:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to load location details'));
    }
  };

  const loadLocationContacts = async (locationId) => {
    try {
      const response = await makeAuthenticatedRequest(`/location-contacts?service_location_id=${locationId}`);
      if (response.success) {
        setLocationContacts(response.data || []);
      }
    } catch (error) {
      console.error('Error loading location contacts:', error);
      setLocationContacts([]);
    }
  };

  const handleLocationClick = async (locationId) => {
    setSelectedLocationId(locationId);
    await loadLocationDetails(locationId);
  };

  const handleBackToList = () => {
    setSelectedLocationId(null);
    setSelectedLocation(null);
    setLocationContacts([]);
  };

  const handleCreateContact = () => {
    setEditingContact(null);
    setContactFormData({
      name: '',
      email: '',
      phone: '',
      title: ''
    });
    setShowContactModal(true);
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setContactFormData({
      name: contact.name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      title: contact.title || ''
    });
    setShowContactModal(true);
  };

  const handleSaveContact = async (e) => {
    e.preventDefault();
    
    if (!contactFormData.name) {
      toast.error('Name is required');
      return;
    }

    try {
      const url = editingContact 
        ? `/location-contacts/${editingContact.id}`
        : '/location-contacts';
      const method = editingContact ? 'PUT' : 'POST';
      
      const body = {
        name: contactFormData.name.trim(),
        service_location_id: selectedLocationId
      };

      if (contactFormData.email?.trim()) {
        body.email = contactFormData.email.trim();
      }
      if (contactFormData.phone?.trim()) {
        body.phone = contactFormData.phone.trim();
      }
      if (contactFormData.title?.trim()) {
        body.title = contactFormData.title.trim();
      }

      const response = await makeAuthenticatedRequest(url, {
        method,
        body: JSON.stringify(body)
      });

      if (response.success) {
        toast.success(editingContact ? 'Location contact updated successfully' : 'Location contact created successfully');
        setShowContactModal(false);
        setEditingContact(null);
        setContactFormData({
          name: '',
          email: '',
          phone: '',
          title: ''
        });
        await loadLocationContacts(selectedLocationId);
      } else {
        toast.error(response.message || 'Failed to save location contact');
      }
    } catch (error) {
      console.error('Error saving location contact:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to save location contact'));
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this location contact?')) {
      return;
    }

    try {
      const response = await makeAuthenticatedRequest(`/location-contacts/${contactId}`, {
        method: 'DELETE'
      });

      if (response.success) {
        toast.success('Location contact deleted successfully');
        await loadLocationContacts(selectedLocationId);
      } else {
        toast.error(response.message || 'Failed to delete location contact');
      }
    } catch (error) {
      console.error('Error deleting location contact:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to delete location contact'));
    }
  };

  const setupAddressAutocomplete = () => {
    if (!mapsInitialized || !addressAutocompleteRef.current) return;

    try {
      if (!window.google?.maps?.places?.Autocomplete) {
        console.error('Google Places Autocomplete is unavailable');
        return;
      }

      const autocompleteInstance = new window.google.maps.places.Autocomplete(
        addressAutocompleteRef.current,
        {
          types: ['establishment', 'geocode'], // Include business names
          componentRestrictions: { country: 'us' }
        }
      );

      autocompleteInstance.addListener('place_changed', () => {
        const place = autocompleteInstance.getPlace();
        if (place && place.geometry && place.geometry.location) {
          const location = place.formatted_address;
          let city = '';
          let state = '';
          let zipCode = '';

          if (place.address_components) {
            place.address_components.forEach(component => {
              const types = component.types;
              if (types.includes('locality')) {
                city = component.long_name;
              } else if (types.includes('administrative_area_level_1')) {
                state = component.short_name;
              } else if (types.includes('postal_code')) {
                zipCode = component.long_name;
              }
            });
          }

          setFormData(prev => ({
            ...prev,
            address: location,
            city,
            state,
            zip_code: zipCode,
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng()
          }));
        }
      });

      setAutocomplete(autocompleteInstance);
    } catch (error) {
      console.error('Error setting up address autocomplete:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.address) {
      toast.error('Address is required');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        ...formData,
        name: formData.name.trim() || DEFAULT_LOCATION_NAME,
        address_2: formData.address_2 === '' ? null : formData.address_2
      };
      const response = await makeAuthenticatedRequest('/service-locations', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (response.success) {
        toast.success('Service location created successfully');
        setShowModal(false);
        resetForm();
        loadLocations();
      } else {
        console.error('Service location creation failed:', response);
        
        // Handle specific backend validation errors
        let validationErrors = null;
        
        if (response.errors && Array.isArray(response.errors)) {
          validationErrors = response.errors;
        } else if (response.error && response.error.errors && Array.isArray(response.error.errors)) {
          validationErrors = response.error.errors;
        } else if (response.data && response.data.errors && Array.isArray(response.data.errors)) {
          validationErrors = response.data.errors;
        }
        
        if (validationErrors && validationErrors.length > 0) {
          console.log('Validation errors received:', validationErrors);
          
          // Check for specific field errors and display them
          const fieldErrors = {};
          validationErrors.forEach(error => {
            if (error.path) {
              fieldErrors[error.path] = error.msg || error.message || 'Invalid value';
            }
          });
          
          // Display specific error messages for each field
          if (fieldErrors.phone_number) {
            toast.error(fieldErrors.phone_number);
          } else if (fieldErrors.name) {
            toast.error(fieldErrors.name);
          } else if (fieldErrors.address) {
            toast.error(fieldErrors.address);
          } else {
            // If no specific field error, show all errors
            const errorMessages = validationErrors.map(error => error.msg || error.message || 'Validation error');
            toast.error(`Please fix the following errors:\n• ${errorMessages.join('\n• ')}`);
          }
        } else {
          toast.error(response.message || 'Failed to create service location');
        }
      }
    } catch (error) {
      console.error('Error creating service location:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to create service location'));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      address_2: '',
      phone_number: '',
      email_address: '',
      city: '',
      state: '',
      zip_code: '',
      latitude: '',
      longitude: ''
    });
    setEditingLocation(null);
    if (addressAutocompleteRef.current) {
      addressAutocompleteRef.current.value = '';
    }
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const searchLower = (searchTerm || '').toLowerCase();
  const filteredLocations = locations.filter((location) => {
    const name = String(location.name ?? '').toLowerCase();
    const address = String(location.address ?? '').toLowerCase();
    const city = String(location.city ?? '').toLowerCase();
    const email = String(location.email_address ?? '').toLowerCase();
    return (
      name.includes(searchLower) ||
      address.includes(searchLower) ||
      city.includes(searchLower) ||
      email.includes(searchLower)
    );
  });

  // Show location details view if a location is selected
  if (selectedLocationId && selectedLocation) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <button
            onClick={handleBackToList}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Service Locations
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{selectedLocation.name}</h1>
              <p className="text-gray-600 mt-2">Location ID: {selectedLocation.id}</p>
            </div>
          </div>
        </div>

        {/* Location Details Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border p-6 mb-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {selectedLocation.address && (
              <div className="flex items-center space-x-3">
                <MapPinIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="font-medium">
                    {selectedLocation.address}
                    {selectedLocation.address_2 && (
                      <>
                        <br />
                        {selectedLocation.address_2}
                      </>
                    )}
                  </p>
                </div>
              </div>
            )}
            
            {(selectedLocation.city || selectedLocation.state) && (
              <div className="flex items-center space-x-3">
                <MapPinIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">City, State</p>
                  <p className="font-medium">
                    {selectedLocation.city && selectedLocation.state 
                      ? `${selectedLocation.city}, ${selectedLocation.state}${selectedLocation.zip_code ? ` ${selectedLocation.zip_code}` : ''}`
                      : selectedLocation.city || selectedLocation.state || 'N/A'
                    }
                  </p>
                </div>
              </div>
            )}
            
            {selectedLocation.phone_number && (
              <div className="flex items-center space-x-3">
                <PhoneIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">{selectedLocation.phone_number}</p>
                </div>
              </div>
            )}

            {selectedLocation.email_address && (
              <div className="flex items-center space-x-3">
                <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{selectedLocation.email_address}</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Location Contacts Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border"
        >
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Location Contacts ({locationContacts.length})
            </h2>
            <button
              onClick={handleCreateContact}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <PlusIcon className="h-4 w-4" />
              <span>Add Contact</span>
            </button>
          </div>

          <div className="divide-y divide-gray-200">
            {locationContacts.length === 0 ? (
              <div className="px-6 py-8 text-center">
                <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No location contacts yet. Click "Add Contact" to create one.</p>
              </div>
            ) : (
              locationContacts.map((contact) => (
                <div key={contact.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-base font-medium text-gray-900">{contact.name}</h3>
                        {contact.title && (
                          <span className="text-sm text-gray-500">- {contact.title}</span>
                        )}
                      </div>
                      <div className="mt-2 space-y-1">
                        {contact.email && (
                          <div className="flex items-center text-sm text-gray-600">
                            <svg className="h-4 w-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            {contact.email}
                          </div>
                        )}
                        {contact.phone && (
                          <div className="flex items-center text-sm text-gray-600">
                            <PhoneIcon className="h-4 w-4 mr-2 text-gray-400" />
                            {contact.phone}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEditContact(contact)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit contact"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteContact(contact.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete contact"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </motion.div>

        {/* Contact Modal */}
        {showContactModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingContact ? 'Edit Location Contact' : 'Add New Location Contact'}
                </h3>
                
                <form onSubmit={handleSaveContact} className="space-y-4">
                  <Input
                    label="Doctor Name"
                    value={contactFormData.name}
                    onChange={(e) => setContactFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Doctor name"
                    required
                  />

                  <Input
                    label="Email (Optional)"
                    type="email"
                    value={contactFormData.email}
                    onChange={(e) => setContactFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="contact@example.com"
                  />

                  <Input
                    label="Phone"
                    type="tel"
                    value={contactFormData.phone}
                    onChange={(e) => setContactFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                  />

                  <SearchableSelect
                    label="Title"
                    value={contactFormData.title}
                    onChange={(e) => setContactFormData(prev => ({ ...prev, title: e.target.value }))}
                    options={[
                      { value: 'Doctor', label: 'Doctor' },
                      { value: 'Physician Assistant', label: 'Physician Assistant' },
                      { value: 'Nurse Practitioner', label: 'Nurse Practitioner' },
                      { value: 'Nurse', label: 'Nurse' },
                      { value: 'Doctor Assistant', label: 'Doctor Assistant' },
                      { value: 'Receptionist', label: 'Receptionist' },
                      { value: 'Office Coordinator', label: 'Office Coordinator' }
                    ]}
                    placeholder="Select title (optional)"
                  />

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowContactModal(false);
                        setEditingContact(null);
                        setContactFormData({
                          name: '',
                          email: '',
                          phone: '',
                          title: ''
                        });
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {editingContact ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Service Locations</h1>
        <p className="text-gray-600 mt-2">Manage service locations for appointments</p>
      </div>

      {/* Header with Search and Add Button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search locations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <Button onClick={openCreateModal} className="flex items-center space-x-2">
          <PlusIcon className="h-5 w-5" />
          <span>Add Location</span>
        </Button>
      </div>

      {/* Locations Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredLocations.map((location) => (
            <motion.div
              key={location.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200 cursor-pointer"
              onClick={() => handleLocationClick(location.id)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <MapPinIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{location.name}</h3>
                    <p className="text-sm text-gray-500">ID: {location.id}</p>
                  </div>
                </div>
                <EyeIcon className="h-5 w-5 text-gray-400" />
              </div>

              <div className="space-y-3">
                <div className="flex items-start space-x-2">
                  <MapPinIcon className="h-4 w-4 text-gray-400 mt-1" />
                  <p className="text-sm text-gray-600">
                    {location.address}
                    {location.address_2 && (
                      <>
                        <br />
                        {location.address_2}
                      </>
                    )}
                  </p>
                </div>

                {location.phone_number && (
                  <div className="flex items-center space-x-2">
                    <PhoneIcon className="h-4 w-4 text-gray-400" />
                    <p className="text-sm text-gray-600">{location.phone_number}</p>
                  </div>
                )}

                {location.email_address && (
                  <div className="flex items-center space-x-2">
                    <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                    <p className="text-sm text-gray-600">{location.email_address}</p>
                  </div>
                )}

                {location.location_contacts && location.location_contacts.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <UserIcon className="h-4 w-4 text-gray-400" />
                    <p className="text-sm text-gray-600">
                      {location.location_contacts.length} contact{location.location_contacts.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                )}

                <div className="text-xs text-gray-400 pt-2 border-t border-gray-100 flex items-center justify-between">
                  <span>Created: {(() => {
                    const dateStr = location.created_at;
                    if (dateStr.includes('-') && !dateStr.includes('T')) {
                      const [year, month, day] = dateStr.split('-').map(Number);
                      return new Date(year, month - 1, day).toLocaleDateString();
                    }
                    return new Date(dateStr).toLocaleDateString();
                  })()}</span>
                  <span className="text-blue-600">Click to manage contacts →</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {!loading && filteredLocations.length === 0 && (
        <div className="text-center py-12">
          <MapPinIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm ? 'No locations found' : 'No service locations'}
          </h3>
          <p className="text-gray-500 mb-4">
            {searchTerm 
              ? 'Try adjusting your search terms' 
              : 'Get started by adding your first service location'
            }
          </p>
          {!searchTerm && (
            <Button onClick={openCreateModal}>
              Add Your First Location
            </Button>
          )}
        </div>
      )}

      {/* Add/Edit Location Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {editingLocation ? 'Edit Service Location' : 'Add New Service Location'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    label="Location Name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={`Defaults to ${DEFAULT_LOCATION_NAME} if blank`}
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      ref={addressAutocompleteRef}
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                      placeholder={mapsInitialized ? "Start typing address or business name..." : "Enter address"}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {mapsInitialized ? (
                      <p className="text-xs text-green-600 mt-1">
                        ✅ Google Maps enabled - Start typing to see address and business suggestions
                      </p>
                    ) : (
                      <p className="text-xs text-orange-600 mt-1">
                        ⚠️ Google Maps Loading - You can still type addresses manually
                      </p>
                    )}
                  </div>

                  <Input
                    label="Apartment Number (Optional)"
                    value={formData.address_2}
                    onChange={(e) => setFormData(prev => ({ ...prev, address_2: e.target.value }))}
                    placeholder="Apt, Suite, Unit, etc."
                  />
                  
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="City"
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="City"
                    />
                    <Input
                      label="State"
                      value={formData.state}
                      onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                      placeholder="State"
                    />
                  </div>
                  
                  <Input
                    label="ZIP Code"
                    value={formData.zip_code}
                    onChange={(e) => setFormData(prev => ({ ...prev, zip_code: e.target.value }))}
                    placeholder="ZIP Code"
                  />
                  
                  <Input
                    label="Phone Number"
                    value={formData.phone_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                    placeholder="(555) 123-4567"
                    required
                  />

                  <Input
                    label="Email Address"
                    type="email"
                    value={formData.email_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, email_address: e.target.value }))}
                    placeholder="location@example.com"
                  />

                  <div className="flex space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setShowModal(false);
                        resetForm();
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="flex-1"
                    >
                      {submitting ? 'Creating...' : 'Create Location'}
                    </Button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ServiceLocations;
